import React, { useState, useRef, useEffect, useCallback } from 'react';
import DOMPurify from 'dompurify';
import SearchBox from './SearchBox';
import { authFetch, authPost } from '../lib/authFetch';
import { sanitizeHtml, escapeHtml } from '../lib/sanitize';
import './AskView.css';

// Word reveal animation - same as NarrativeFeed
function WordReveal({ html, delay = 0 }) {
  const [visibleWords, setVisibleWords] = useState(0);

  const parseHtml = (htmlString) => {
    const temp = document.createElement('div');
    temp.innerHTML = DOMPurify.sanitize(htmlString);
    const words = [];

    const processNode = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent;
        text.split(/(\s+)/).forEach(word => {
          if (word.trim()) {
            words.push({ type: 'text', content: word });
          } else if (word) {
            words.push({ type: 'space', content: word });
          }
        });
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const className = node.className;
        node.childNodes.forEach(child => {
          if (child.nodeType === Node.TEXT_NODE) {
            child.textContent.split(/(\s+)/).forEach(word => {
              if (word.trim()) {
                words.push({ type: 'element', className, content: word });
              } else if (word) {
                words.push({ type: 'space', content: word });
              }
            });
          }
        });
      }
    };

    temp.childNodes.forEach(processNode);
    return words;
  };

  const words = parseHtml(html);
  const totalWords = words.filter(w => w.type !== 'space').length;

  useEffect(() => {
    setVisibleWords(0);
    const startTimeout = setTimeout(() => {
      const interval = setInterval(() => {
        setVisibleWords(prev => {
          if (prev >= totalWords) {
            clearInterval(interval);
            return prev;
          }
          return prev + 1;
        });
      }, 35);
      return () => clearInterval(interval);
    }, delay);
    return () => clearTimeout(startTimeout);
  }, [html, delay, totalWords]);

  let wordIndex = 0;
  return (
    <span>
      {words.map((word, i) => {
        if (word.type === 'space') return <span key={i}>{word.content}</span>;
        const isVisible = wordIndex < visibleWords;
        wordIndex++;
        return (
          <span key={i} className={`word-reveal ${word.className || ''} ${isVisible ? 'visible' : ''}`}>
            {word.content}
          </span>
        );
      })}
    </span>
  );
}

function AskView({
  dataMode,
  onAction,
  conversationHistory,
  onAddMessage,
  groups = [],
  onNavigate,
  dataLoaded = false
}) {
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [, setStreamingText] = useState('');
  const [donorNames, setDonorNames] = useState([]);
  const [listNames, setListNames] = useState([]);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [expandedUserMessages, setExpandedUserMessages] = useState({});
  const [animatingMessageId, setAnimatingMessageId] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const isScrolledRef = useRef(false);
  const fileInputRef = useRef(null);
  const scrollTimeoutRef = useRef(null);
  const containerRef = useRef(null);

  // Fetch donor names and list names for linking
  const fetchNamesForLinking = useCallback(async () => {
    try {
      console.log('fetchNamesForLinking called, dataMode:', dataMode);
      const [donorsRes, listsRes] = await Promise.all([
        authFetch(`/api/donors?mode=${dataMode}`),
        authFetch(`/api/lists?mode=${dataMode}`)
      ]);

      console.log('donorsRes.ok:', donorsRes.ok, 'listsRes.ok:', listsRes.ok);

      if (donorsRes.ok) {
        const data = await donorsRes.json();
        console.log('donors API response:', data);
        const names = Object.keys(data.donors || {});
        console.log('extracted donor names:', names.length, names.slice(0, 5));
        setDonorNames(names);
      } else {
        console.log('donors fetch failed:', donorsRes.status);
      }
      if (listsRes.ok) {
        const data = await listsRes.json();
        console.log('lists API response:', data);
        setListNames((data.lists || []).map(l => l.name));
      } else {
        console.log('lists fetch failed:', listsRes.status);
      }
    } catch (error) {
      console.log('Could not fetch names for linking:', error);
    }
  }, [dataMode]);

  useEffect(() => {
    fetchNamesForLinking();
  }, [fetchNamesForLinking]);

  // Cleanup scroll timeout on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  // Track scroll to shrink search bar (with hysteresis to prevent flicker)
  const handleConversationScroll = (e) => {
    const scrollTop = e.target.scrollTop;
    // Use different thresholds for scrolling down vs up to prevent flickering
    const shouldBeScrolled = isScrolledRef.current
      ? scrollTop > 5    // Stay scrolled until we're very close to top
      : scrollTop > 20;  // Only trigger scroll state after scrolling a bit
    if (shouldBeScrolled !== isScrolledRef.current) {
      isScrolledRef.current = shouldBeScrolled;
      setIsScrolled(shouldBeScrolled);
    }
  };

  const scrollToTop = () => {
    if (containerRef.current) {
      containerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSubmit = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput('');
    setIsLoading(true);
    setStreamingText('');

    // Add user message to history
    onAddMessage({ role: 'user', content: userMessage, timestamp: new Date().toISOString() });

    try {
      const response = await authPost('/api/universal-chat-stream', {
        message: userMessage,
        conversationHistory: conversationHistory.slice(-10),
        mode: dataMode
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullResponse = '';
      let finalData = null;
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        // A single SSE event can be split across two reads by the underlying TCP
        // stream - buffer any incomplete trailing line instead of dropping it.
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));

              if (data.type === 'chunk') {
                fullResponse += data.text;
                // Display streamed text directly (server now sends plain text, not JSON)
                setStreamingText(fullResponse);
              } else if (data.type === 'done') {
                finalData = data;
              } else if (data.type === 'error') {
                throw new Error(data.message);
              }
            } catch (parseError) {
              // Ignore parse errors for incomplete chunks
            }
          }
        }
      }

      setStreamingText('');

      if (finalData) {
        console.log('AskView finalData:', finalData);
        console.log('AskView finalData.action:', finalData.action);
        // Add assistant response to history
        // Action is stored with the message so user can click to navigate
        const messageTimestamp = new Date().toISOString();
        setAnimatingMessageId(messageTimestamp);
        onAddMessage({
          role: 'assistant',
          content: finalData.response,
          action: finalData.action,
          messageType: finalData.messageType,
          timestamp: messageTimestamp
        });

        // Clear animation state after animation completes (15ms per 2 chars + buffer)
        const animationDuration = Math.ceil(finalData.response.length / 2) * 15 + 500;
        setTimeout(() => setAnimatingMessageId(null), animationDuration);

        // Refresh donor/list names in case new ones were added
        fetchNamesForLinking();

        // Trigger the action handler (for Supabase sync and navigation)
        if (finalData.action && onAction) {
          console.log('AskView calling onAction with:', finalData.action);
          // Pass the original user message along with the action
          onAction({ ...finalData.action, originalMessage: userMessage });
        }
      }

    } catch (error) {
      console.error('Chat error:', error);
      setStreamingText('');
      onAddMessage({
        role: 'assistant',
        content: "Sorry, I couldn't process that request. Please try again.",
        timestamp: new Date().toISOString()
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadStatus('uploading');
    setIsLoading(true);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('mode', dataMode);

    try {
      const response = await authFetch('/api/import-donors', {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (response.ok) {
        setUploadStatus(`Added ${data.count} people`);
        fetchNamesForLinking(); // Refresh donor list
        setTimeout(() => setUploadStatus(null), 3000);
      } else {
        setUploadStatus('Upload failed');
        setTimeout(() => setUploadStatus(null), 3000);
      }
    } catch (error) {
      console.error('Upload error:', error);
      setUploadStatus('Upload failed');
      setTimeout(() => setUploadStatus(null), 3000);
    } finally {
      setIsLoading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  // escapeHtml imported from ../lib/sanitize

  // Make donor/list names clickable - simplified to match working ChatHistory.js
  const highlightText = (text) => {
    if (!text) return '';
    let result = escapeHtml(text);

    // Convert markdown bold to HTML bold for better display
    result = result.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');

    // Build a map of first names to full names for matching
    const firstNameToFullName = {};
    for (const fullName of donorNames) {
      const firstName = fullName.split(' ')[0];
      if (firstName && firstName.length >= 3) {
        // If multiple people have same first name, prefer longest full name
        if (!firstNameToFullName[firstName] || fullName.length > firstNameToFullName[firstName].length) {
          firstNameToFullName[firstName] = fullName;
        }
      }
    }

    // Make full donor names clickable first (sort by length descending to match longer names first)
    const sortedDonorNames = [...donorNames].sort((a, b) => b.length - a.length);
    for (const name of sortedDonorNames) {
      if (name.length < 3) continue;
      const escapedName = escapeHtml(name);
      const namePattern = new RegExp(`\\b(${escapedName.replace(/[.*+?^${}()|[\\]\\\\]/g, '\\$&')})\\b`, 'gi');
      result = result.replace(namePattern, `<span class="clickable-name donor-link" data-donor="${escapeHtml(name)}">$1</span>`);
    }

    // Then match first names (that weren't already matched as part of full names)
    const sortedFirstNames = Object.keys(firstNameToFullName).sort((a, b) => b.length - a.length);
    for (const firstName of sortedFirstNames) {
      if (firstName.length < 3) continue;
      const fullName = firstNameToFullName[firstName];
      const escapedFirstName = escapeHtml(firstName);
      // Match first name only if not already wrapped in a span (check for preceding >)
      const namePattern = new RegExp(`(^|[^">])\\b(${escapedFirstName.replace(/[.*+?^${}()|[\\]\\\\]/g, '\\$&')})\\b(?!<\\/span>)`, 'gi');
      result = result.replace(namePattern, `$1<span class="clickable-name donor-link" data-donor="${escapeHtml(fullName)}">$2</span>`);
    }

    // Make list names clickable
    const sortedListNames = [...listNames].sort((a, b) => b.length - a.length);
    for (const name of sortedListNames) {
      if (name.length < 3) continue;
      const escapedName = escapeHtml(name);
      const namePattern = new RegExp(`\\b(${escapedName.replace(/[.*+?^${}()|[\\]\\\\]/g, '\\$&')})\\b`, 'gi');
      result = result.replace(namePattern, `<span class="clickable-name list-link" data-list="$1">$1</span>`);
    }

    // Final sanitization pass
    return sanitizeHtml(result);
  };

  // Handle clicks on donor/list names in message content
  const handleContentClick = (e) => {
    const target = e.target;

    if (target.classList.contains('donor-link')) {
      e.stopPropagation(); // Prevent message-level click
      const donorName = target.getAttribute('data-donor');
      if (donorName) {
        onAction({ type: 'select_donor', data: { name: donorName } });
      }
    } else if (target.classList.contains('list-link')) {
      e.stopPropagation();
      const listName = target.getAttribute('data-list');
      if (listName) {
        onAction({ type: 'navigate', view: 'lists', listName });
      }
    }
  };

  const uploadButton = (
    <>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileUpload}
        accept=".csv,.txt,.json,.xlsx,.xls"
        style={{ display: 'none' }}
      />
      <button
        className="upload-button"
        onClick={() => fileInputRef.current?.click()}
        title="Upload file to add people"
      >
        +
      </button>
      {uploadStatus && (
        <span className="upload-status">{uploadStatus}</span>
      )}
    </>
  );

  // Filter conversation history to only show messages from the last 3 days
  const threeDaysAgo = new Date();
  threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);

  const recentHistory = conversationHistory.filter(msg => {
    if (!msg.timestamp) return true; // Keep messages without timestamps (legacy)
    return new Date(msg.timestamp) >= threeDaysAgo;
  });

  const hasHistory = recentHistory.length > 0;

  // Detect if user is brand new (no groups, no donors, no history)
  // Wait for all data to load before showing to avoid flash
  const isNewUser = dataLoaded && !hasHistory && groups.length === 0;

  return (
    <div ref={containerRef} className={`ask-view ${hasHistory ? 'has-history' : ''}`} onScroll={hasHistory ? handleConversationScroll : undefined}>
      {/* Welcome text for new users */}
      {isNewUser && (
        <h1 className="welcome-title rainbow-text">Tell me about someone...</h1>
      )}
      <div className={`search-container tour-search-box ${hasHistory ? 'collapsed' : ''} ${isScrolled ? 'scrolled' : ''}`}>
        <SearchBox
          placeholder={isNewUser ? "Met George Washington for dinner last week. A likely investor" : "Ask or tell me anything about your people, lists, plans, etc."}
          rainbowPlaceholder={true}
          value={input}
          onChange={setInput}
          onSearch={handleSubmit}
          expandable={true}
          donors={donorNames}
          leftAction={uploadButton}
          loading={isLoading}
          showExamples={false}
          noHoverEffect={hasHistory}
        />
      </div>


      {/* Scroll to top button - centered below chatbar */}
      {isScrolled && hasHistory && (
        <button className="scroll-to-top" onClick={scrollToTop} aria-label="Scroll to top">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M18 15l-6-6-6 6" />
          </svg>
        </button>
      )}

      {/* Conversation history */}
      {hasHistory && (
        <div className="ask-conversation">
          <div className="ask-messages">
            {isLoading && (
              <div className="ask-message assistant loading">
                <div className="ask-message-header">
                  <span className="ask-message-role">knowroom</span>
                </div>
                <div className="ask-typing-indicator">
                  <span></span><span></span><span></span>
                </div>
              </div>
            )}
            {[...recentHistory].reverse().map((msg, index) => {
              // Determine which view to navigate to based on action type
              const getTargetForAction = (action) => {
                if (!action) return null;
                switch (action.type) {
                  case 'navigate': return { view: action.view || action.data?.view };
                  case 'search': return { view: action.view || action.data?.view || 'people' };
                  case 'create_list': return { view: 'lists', action: action }; // Pass full action to recreate list
                  case 'select_donor':
                  case 'add_donor':
                    return { view: 'people', action: action }; // Pass action to show donor's narrative
                  case 'update_donor':
                    return null; // Don't show action link - names are clickable in the content
                  default: return null;
                }
              };
              const target = getTargetForAction(msg.action);

              const isUser = msg.role === 'user';
              const isExpanded = expandedUserMessages[index];

              return (
                <div
                  key={index}
                  className={`ask-message ${msg.role} ${target ? 'clickable' : ''} ${isUser && !isExpanded ? 'collapsed' : ''}`}
                  onClick={() => {
                    if (isUser) {
                      // Toggle expansion for user messages on mobile
                      setExpandedUserMessages(prev => ({
                        ...prev,
                        [index]: !prev[index]
                      }));
                    } else if (target) {
                      if (target.action) {
                        // For create_list, re-execute to show the same list
                        onAction(target.action);
                      } else {
                        onAction({ type: 'navigate', view: target.view });
                      }
                    }
                  }}
                >
                  <div className="ask-message-header">
                    <span className="ask-message-role">{isUser ? 'You' : 'knowroom'}</span>
                    <span className="ask-message-time">{formatTime(msg.timestamp)}</span>
                    {isUser && (
                      <span className="ask-message-expand-icon">
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <polyline points={isExpanded ? "18 15 12 9 6 15" : "6 9 12 15 18 9"} />
                        </svg>
                      </span>
                    )}
                  </div>
                  <div className="ask-message-content" onClick={handleContentClick}>
                    {msg.timestamp === animatingMessageId && !isUser ? (
                      (() => {
                        const paragraphs = (msg.content || '').split('\n\n');
                        let cumulativeDelay = 0;
                        return paragraphs.map((paragraph, pIndex) => {
                          const delay = cumulativeDelay;
                          const wordCount = paragraph.split(/\s+/).filter(w => w.trim()).length;
                          cumulativeDelay += wordCount * 35 + 150;
                          // Apply same formatting as highlightText
                          const formattedParagraph = highlightText(paragraph);
                          return <p key={pIndex}><WordReveal html={formattedParagraph} delay={delay} /></p>;
                        });
                      })()
                    ) : (
                      (msg.content || '').split('\n\n').map((paragraph, pIndex) => (
                        <p key={pIndex} dangerouslySetInnerHTML={{ __html: highlightText(paragraph) }} />
                      ))
                    )}
                  </div>
                  {target && (
                    <div className="ask-message-action">
                      <span>Go to {target.view}</span>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M5 12h14M12 5l7 7-7 7" />
                      </svg>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default AskView;
